import { ReactNode } from 'react';

import {
  ChangeConfirmationPolicyData,
  ConfirmSeverity,
} from '@/react/portainer/policies/components/change-confirmation/types';
import { getChangeConfirmationPolicyFromCache } from '@/react/portainer/policies/utils/change-confirmation/getChangeConfirmationPolicyFromCache';
import { notifyDialogClosed } from '@/react/portainer/policies/utils/change-confirmation/shouldSkipConfirm';
import { SnoozeInput } from '@/react/portainer/policies/utils/change-confirmation/SnoozeInput';
import { isSnoozeActive } from '@/react/portainer/policies/utils/change-confirmation/snooze-storage';

import { Card } from '@@/Card/Card';

import { openDialog, DialogOptions } from './Dialog';
import { OnSubmit, ModalType } from './Modal';
import { ButtonOptions } from './types';
import { buildCancelButton, buildConfirmButton } from './utils';
import { linkifyMessage } from './linkifyMessage';

export type ConfirmCallback = OnSubmit<boolean>;

export interface ConfirmOptions
  extends Omit<DialogOptions<boolean>, 'title' | 'message' | 'buttons'> {
  title?: string;
  message?: ReactNode;
  confirmButton?: ButtonOptions<true>;
  cancelButtonLabel?: string;
}

export async function openConfirm({
  confirmButton = buildConfirmButton(),
  cancelButtonLabel,
  ...options
}: ConfirmOptions) {
  const confirmPolicy = getChangeConfirmationPolicyFromCache();
  const derivedOptions = deriveDialogOptions(options, confirmPolicy);
  const result = await openDialog({
    ...derivedOptions,
    buttons: [buildCancelButton(cancelButtonLabel), confirmButton],
  });
  notifyDialogClosed();
  return !!result;
}

export function confirm(options: ConfirmOptions) {
  return openConfirm(options);
}

export function confirmDestructive(options: Omit<ConfirmOptions, 'modalType'>) {
  return openConfirm({
    ...options,
    modalType: ModalType.Destructive,
  });
}

const confirmWebEditorDiscardOptions = {
  modalType: ModalType.Warn,
  title: 'Are you sure?',
  message:
    'You currently have unsaved changes in the editor. Are you sure you want to leave?',
};

const confirmGenericDiscardOptions = {
  modalType: ModalType.Warn,
  title: 'Are you sure?',
  message:
    'You currently have unsaved changes. Are you sure you want to leave?',
};

export function confirmWebEditorDiscard() {
  return openConfirm({
    ...confirmWebEditorDiscardOptions,
    confirmButton: buildConfirmButton('Yes', 'danger'),
  });
}

export function confirmGenericDiscard() {
  return openConfirm({
    ...confirmGenericDiscardOptions,
    confirmButton: buildConfirmButton('Yes', 'danger'),
  });
}

export function confirmDelete(message: ReactNode) {
  return confirmDestructive({
    title: 'Are you sure?',
    message,
    confirmButton: buildConfirmButton('Remove', 'danger'),
  });
}

export async function confirmUpdate(
  message: string,
  callback: ConfirmCallback
) {
  const result = await openConfirm({
    title: 'Are you sure?',
    modalType: ModalType.Warn,
    message,
    confirmButton: buildConfirmButton('Update'),
  });

  callback(result);

  return result;
}

const confirmChangePasswordOptions = {
  modalType: ModalType.Warn,
  title: 'Are you sure?',
  message:
    'You will be logged out after the password change. Do you want to change your password?',
};

export function confirmChangePassword() {
  return openConfirm({
    ...confirmChangePasswordOptions,
    confirmButton: buildConfirmButton('Change'),
  });
}

export function deriveDialogOptions(
  options: ConfirmOptions,
  confirmPolicy: ChangeConfirmationPolicyData | null
) {
  // even when the snooze is active, show the original message (just don't show any additional UI)
  if (!confirmPolicy || isSnoozeActive()) {
    return {
      ...options,
      message: options.message ?? '',
    };
  }

  // ignore discard and change passowrd dialogs
  const passthroughDialogOptions = [
    confirmChangePasswordOptions,
    confirmGenericDiscardOptions,
    confirmWebEditorDiscardOptions,
  ];
  const isPassthroughDialog = passthroughDialogOptions.some(
    (opt) => opt.title === options.title && opt.message === options.message
  );
  if (isPassthroughDialog) {
    return {
      ...options,
      message: options.message ?? '',
    };
  }

  const confirmEnabled = confirmPolicy.ConfirmEnabled.value;
  const confirmTitle = confirmPolicy.ConfirmTitle.value;
  const confirmMessage = confirmPolicy.ConfirmMessage.value;
  const confirmSeverity = confirmPolicy.ConfirmSeverity.value;
  if (!confirmEnabled || (!confirmTitle && !confirmMessage)) {
    return {
      ...options,
      message: options.message ?? '',
    };
  }

  return {
    // keeping existing options here will preserve the custom button options
    ...options,
    title: confirmTitle ?? options.title,
    message: buildMessage(),
    modalType: deriveModalType(),
  };

  // use the policy message by default. If there is a custom message, show if with less emphasis.
  // URLs and hostnames in the message are rendered as safe links.
  function buildMessage(): ReactNode {
    // convert URLs to external links
    const linkifiedPolicyMessage = linkifyMessage(confirmMessage);

    return (
      <>
        {/* policy message */}
        <p className="!mb-0">{linkifiedPolicyMessage}</p>
        <SnoozeInput />
        {/* original confirmation message for context */}
        {!!options.message && (
          <Card>
            <p className="text-muted !mb-0">{options.message}</p>
          </Card>
        )}
      </>
    );
  }

  function deriveModalType(): ModalType | undefined {
    const SEVERITY_TO_MODAL_TYPE: Record<
      ConfirmSeverity,
      ModalType | undefined
    > = {
      default: undefined,
      warning: ModalType.Warn,
      error: ModalType.Destructive,
    };

    if (confirmSeverity) {
      return SEVERITY_TO_MODAL_TYPE[confirmSeverity];
    }

    return options.modalType;
  }
}
